import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { DS } from '@/design-system'
import { SettingsCard } from '@/components/layouts/SettingsCard'
import { Button } from '@/components/ANewDesign/Button/Button'
import { FormTextInput } from './components/FormTextInput'
import { InfoRow } from './components/InfoRow'
import { SelectPicker } from './components/SelectPicker'
import { TurnClockPicker } from './components/TurnClockPicker'
import { BlindIntervalPicker } from './components/BlindIntervalPicker'
import { Tooltip } from './components/Tooltip'
import { translate } from '../../i18n'
import { useCreateGameStore, type SoloAdvisorLevel } from '../../stores/create-game-store'
import { ACTION_ADVISOR_COPY } from '@/screens/GameContainer/actionAdvisor.constants'
import { numberWithCommas } from '@/utils/numberWithCommas'

const BOT_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']
const BOT_SKILL_OPTIONS = ['Novice', 'Easy', 'Hard', 'Expert', 'Mix']
const BLIND_OPTIONS = ['200/100', '400/200', '500/250', '1,000/500']


// The three advisor coaching levels from the in-game drawer (same copy):
// Advice & Feedback (advice before each play + feedback), Feedback Only, Off.
// The summary view applies the default automatically; customize picks one.
const ADVISOR_LEVEL_OPTIONS = [
  { value: 'beginner', label: ACTION_ADVISOR_COPY.advisorBeginner },
  { value: 'intermediate', label: ACTION_ADVISOR_COPY.advisorIntermediate },
  { value: 'off', label: ACTION_ADVISOR_COPY.advisorOff }
] as const satisfies ReadonlyArray<{ value: SoloAdvisorLevel; label: string }>
const advisorLevelLabel = (level: SoloAdvisorLevel) =>
  ADVISOR_LEVEL_OPTIONS.find((option) => option.value === level)?.label ?? ACTION_ADVISOR_COPY.advisorBeginner
export interface SoloTabHandle {
  /** Validates starting chips, surfacing an inline field error. Returns whether the form is
   * valid so callers (the footer button in index.tsx) can bail out before submitting. */
  validate: () => boolean
}

interface SoloTabProps {
  allowCustomization?: boolean
}

export const SoloTab = forwardRef<SoloTabHandle, SoloTabProps>(function SoloTab({ allowCustomization = true }, ref) {
  const [customizing, setCustomizing] = useState(false)
  const [chipsError, setChipsError] = useState<string | undefined>()
  const { solo, setSolo, resetSolo } = useCreateGameStore()

  const validateChips = (bigBlind = solo.bigBlind) => {
    const minChips = bigBlind * 2
    const chipsErr =
      solo.startingChips >= minChips
        ? undefined
        : translate('createGameV2.solo.chipsRequired', { min: numberWithCommas(minChips) })
    setChipsError(chipsErr)
    return chipsErr
  }

  useImperativeHandle(ref, () => ({
    validate: () => {
      const chipsErr = validateChips()
      if (chipsErr) setCustomizing(true)
      return !chipsErr
    }
  }))

  return (
    <SettingsCard gap={24} style={{ borderRadius: 16, borderWidth: 0, padding: 24 }}>
      <FormTextInput
        label={translate('createGameV2.solo.gameName')}
        value={solo.gameName}
        onChangeText={(text) => setSolo({ gameName: text })}
        placeholder={translate('createGameV2.solo.gameNamePlaceholder')}
      />

      <DS.Frame row gap="20">
        <View style={styles.dropdownCol}>
          <DS.Frame row alignItems="center" gap="4">
            <DS.Text variant="Body Medium/Regular">{translate('createGameV2.solo.bots')}</DS.Text>
            <Tooltip text={translate('createGameV2.solo.tooltips.bots')}>
              <DS.Icon name="Info--filled" width={16} height={16} />
            </Tooltip>
          </DS.Frame>
          <SelectPicker
            data={BOT_OPTIONS}
            defaultValue={String(solo.botCount)}
            onSelect={(val) => setSolo({ botCount: Number(val) })}
          />
        </View>
        <View style={styles.dropdownCol}>
          <DS.Frame row alignItems="center" gap="4">
            <DS.Text variant="Body Medium/Regular">{translate('createGameV2.solo.botSkill')}</DS.Text>
            <Tooltip text={translate('createGameV2.solo.tooltips.botSkill')}>
              <DS.Icon name="Info--filled" width={16} height={16} />
            </Tooltip>
          </DS.Frame>
          <SelectPicker
            data={BOT_SKILL_OPTIONS}
            defaultValue={solo.botSkill}
            onSelect={(val) => setSolo({ botSkill: val as typeof solo.botSkill })}
          />
        </View>
      </DS.Frame>

      {!customizing ? (
        <>
          <DS.Text variant="Caption/Bold" style={styles.appliedLabel}>
            {translate('createGameV2.solo.appliedAutomatically')}
          </DS.Text>

          <DS.Frame gap="8">
            <InfoRow
              label={translate('createGameV2.solo.startingChips')}
              value={numberWithCommas(solo.startingChips)}
            />
            <InfoRow label={translate('createGameV2.solo.blinds')} value={`${solo.bigBlind / 2}/${solo.bigBlind}`} />
            <InfoRow label={translate('createGameV2.solo.turnClock')} value={`${solo.turnDuration} sec`} />
            <InfoRow
              label={translate('createGameV2.solo.blindIncreases')}
              value={
                solo.blindIncreases
                  ? translate('createGameV2.minutesCount', { count: solo.blindInterval })
                  : translate('createGameV2.none')
              }
            />
            <InfoRow
              label={translate('createGameV2.solo.advisor')}
              value={advisorLevelLabel(solo.advisorLevel)}
            />
          </DS.Frame>

          { allowCustomization && (<>
            <View style={[styles.separator, { backgroundColor: DS.palette['Brand/Light Grey'] }]} />
            <Button
              type="outline"
              size="large"
              text={translate('createGameV2.solo.customize')}
              onPress={() => setCustomizing(true)}
              containerStyle={[{ paddingVertical: 14, borderColor: DS.palette['Brand/Light Grey'] }]}
              textStyle={[{ fontWeight: '500' }]}
            />
          </>)}
        </>
      ) : (
        <DS.Frame gap="24">
          <DS.Frame gap="8">
            <DS.Frame row gap="20">
              <View style={styles.dropdownCol}>
                <FormTextInput
                  label={translate('createGameV2.solo.startingChips')}
                  value={numberWithCommas(solo.startingChips)}
                  onChangeText={(val) => {
                    const num = Number(val.replace(/\D/g, ''))
                    if (!isNaN(num)) setSolo({ startingChips: num })
                    if (chipsError) setChipsError(undefined)
                  }}
                  keyboardType="numeric"
                  error={chipsError}
                  hideErrorText
                  onBlur={() => validateChips()}
                />
              </View>
              <View style={styles.dropdownCol}>
                <DS.Frame row alignItems="center" gap="4">
                  <DS.Text variant="Body Medium/Regular">{translate('createGameV2.solo.startingBlind')}</DS.Text>
                  <Tooltip text={translate('createGameV2.solo.tooltips.startingBlind')}>
                    <DS.Icon name="Info--filled" width={16} height={16} />
                  </Tooltip>
                </DS.Frame>
                <SelectPicker
                  data={BLIND_OPTIONS}
                  defaultValue={`${numberWithCommas(solo.bigBlind)}/${numberWithCommas(Math.floor(solo.bigBlind / 2))}`}
                  onSelect={(val) => {
                    const newBigBlind = Number(val.split('/')[0].replace(/,/g, ''))
                    setSolo({ bigBlind: newBigBlind })
                    validateChips(newBigBlind)
                  }}
                />
              </View>
            </DS.Frame>
            {chipsError ? (
              <DS.Text variant="Caption/Regular" style={{ color: DS.palette['Utility/Error'], fontSize: 16 }}>
                {chipsError}
              </DS.Text>
            ) : null}
          </DS.Frame>

          <DS.Frame gap="8">
            <DS.Text variant="Body Medium/Regular">{translate('createGameV2.solo.blindIncreasesInterval')}</DS.Text>
            <BlindIntervalPicker
              value={solo.blindIncreases ? solo.blindInterval : null}
              onChange={(minutes) =>
                setSolo({ blindIncreases: minutes !== null, blindInterval: minutes ?? solo.blindInterval })
              }
            />
          </DS.Frame>

          <DS.Frame gap="8">
            <DS.Text variant="Body Medium/Regular">{translate('createGameV2.solo.turnClock')}</DS.Text>
            <TurnClockPicker value={solo.turnDuration} onChange={(val) => setSolo({ turnDuration: val })} />
          </DS.Frame>

          <DS.Frame gap="8">
            <DS.Frame row alignItems="center" gap="4">
              <DS.Text variant="Body Medium/Regular">{translate('createGameV2.solo.advisor')}</DS.Text>
              <Tooltip text={translate('createGameV2.solo.tooltips.advisor')}>
                <DS.Icon name="Info--filled" width={16} height={16} />
              </Tooltip>
            </DS.Frame>
            {/* The three levels from the in-game drawer (same labels); the
                picked level is applied to the advisor when the game starts
                (see handleStartSoloGame in index.tsx). */}
            <SelectPicker
              data={ADVISOR_LEVEL_OPTIONS.map((option) => option.label)}
              defaultValue={advisorLevelLabel(solo.advisorLevel)}
              onSelect={(label) => {
                const level = ADVISOR_LEVEL_OPTIONS.find((option) => option.label === label)?.value
                if (level) setSolo({ advisorLevel: level })
              }}
            />
          </DS.Frame>

          <Button
            type="outline"
            size="large"
            text={translate('createGameV2.solo.reset')}
            onPress={() => {
              resetSolo()
              setChipsError(undefined)
              setCustomizing(false)
            }}
            containerStyle={[{ paddingVertical: 14, borderColor: DS.palette['Brand/Light Grey'] }]}
            textStyle={[{ fontWeight: '500' }]}
          />
        </DS.Frame>
      )}
    </SettingsCard>
  )
})

const styles = StyleSheet.create({
  separator: {
    height: 1,
    backgroundColor: DS.palette['Brand/Light Grey']
  },
  appliedLabel: {
    color: DS.palette['Brand/Dark Grey'],
    letterSpacing: 0.5
  },
  dropdownCol: {
    flex: 1
  }
})
